/* Settings tab: class sections per level per school year + teacher-per-subject
 * assignments (with Professeur Principal). */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sectionsApi, classTeachersApi, teachersApi, gradingApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { CheckCircle, Users, ChalkboardTeacher, Star, FilePdf, Clock } from '@phosphor-icons/react';

const CYCLE_LABELS = { maternelle: 'Maternelle', primaire: 'Primaire', college: 'Collège', lycee: 'Lycée' };

const SectionsAndTeachersTab = ({ schoolYear }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [savingLevel, setSavingLevel] = useState(null);
  // Tracks the last count persisted to the backend, keyed by class_level.
  // We need this because `sections` state is updated on every keystroke so
  // `s.count` inside the onBlur closure always matches the input, defeating
  // any change-detection. This ref stores the server-truth count and lets us
  // decide reliably whether to POST on blur.
  const persistedCountsRef = useRef({});

  // Teacher-assignment side
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [classes, setClasses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [templateSubjects, setTemplateSubjects] = useState([]);
  const [loadingCls, setLoadingCls] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, c] = await Promise.all([
        sectionsApi.list(schoolYear),
        teachersApi.getAll(),
        gradingApi.classConfigs(schoolYear),
      ]);
      setSections(s.data || []);
      // Cache persisted counts for reliable change-detection on blur.
      const persisted = {};
      (s.data || []).forEach(row => { persisted[row.class_level] = row.count; });
      persistedCountsRef.current = persisted;
      setTeachers(t.data || []);
      // Class list = configured classes so subject templates are known
      setClasses((c.data || []).map((x) => x.class_name).sort());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [schoolYear]);

  useEffect(() => { load(); }, [load]);

  const setCount = async (level, count) => {
    setSavingLevel(level);
    try {
      const { data } = await sectionsApi.save({ class_level: level, school_year: schoolYear, count });
      // Update local state without re-loading everything (avoids losing focus/UI state).
      setSections(prev => prev.map(x =>
        x.class_level === level ? { ...x, count: data.count, sub_labels: data.sub_labels } : x,
      ));
      persistedCountsRef.current[level] = data.count;
      toast.success(`${level} : ${data.count} section${data.count > 1 ? 's' : ''} enregistrée${data.count > 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
      // Rollback to server truth on error
      const persisted = persistedCountsRef.current[level];
      if (persisted != null) {
        setSections(prev => prev.map(x =>
          x.class_level === level ? { ...x, count: persisted } : x,
        ));
      }
    } finally { setSavingLevel(null); }
  };

  const applyAll = async () => {
    if (!window.confirm('Appliquer les sections ? Les classes existantes vides seront supprimées, celles avec des élèves seront conservées.')) return;
    setApplying(true);
    try {
      const { data } = await sectionsApi.apply(schoolYear);
      toast.success(`${data.created.length} classe(s) créée(s), ${data.kept.length} conservée(s), ${data.skipped_delete_non_empty.length} avec élèves protégées`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setApplying(false); }
  };

  const downloadDistribution = async () => {
    try {
      const { data } = await sectionsApi.pedagogicalDistributionPdf(schoolYear);
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `repartition_pedagogique_${schoolYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur téléchargement PDF');
    }
  };

  const downloadWorkload = async () => {
    try {
      const { data } = await sectionsApi.teacherWorkloadPdf(schoolYear);
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `charge_horaire_enseignants_${schoolYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur téléchargement PDF');
    }
  };

  // ----- Teacher assignments -----
  const loadAssignments = useCallback(async (className) => {
    if (!className) return;
    setLoadingCls(true);
    try {
      const [aRes, tmplRes] = await Promise.all([
        classTeachersApi.list(className),
        gradingApi.templates(),
      ]);
      setAssignments(aRes.data || []);
      // pick template for this class
      const cfg = (await gradingApi.classConfigs(schoolYear)).data || [];
      const clsCfg = cfg.find(x => x.class_name === className);
      const tmpl = tmplRes.data?.find?.(x => x.key === clsCfg?.template_key)
                || tmplRes.data?.templates?.find?.(x => x.key === clsCfg?.template_key);
      setTemplateSubjects((tmpl?.subjects || []).map(s => s.name));
    } catch (e) {
      toast.error('Erreur chargement affectations');
    } finally { setLoadingCls(false); }
  }, [schoolYear]);

  useEffect(() => { if (selectedClass) loadAssignments(selectedClass); }, [selectedClass, loadAssignments]);

  const assignTeacher = async (subject, teacherId, isPrincipal) => {
    if (!teacherId) {
      // remove
      try {
        await classTeachersApi.remove(selectedClass, subject);
        await loadAssignments(selectedClass);
      } catch (e) { toast.error('Erreur suppression'); }
      return;
    }
    try {
      await classTeachersApi.save({
        class_name: selectedClass,
        subject,
        teacher_id: teacherId,
        is_principal: !!isPrincipal,
      });
      toast.success('Enseignant affecté');
      await loadAssignments(selectedClass);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const byCycle = useMemo(() => {
    const map = {};
    sections.forEach(s => { (map[s.cycle] ||= []).push(s); });
    return map;
  }, [sections]);

  const getAssignment = (subject) => assignments.find(a => a.subject === subject) || {};
  const cyclesInOrder = ['maternelle', 'primaire', 'college', 'lycee'];
  const isSingleTeacher = classes.length > 0 && (() => {
    // Heuristic: Maternelle+Primaire show only 1 subject (or the teacher is auto principal)
    const cls = classes.find(c => c === selectedClass);
    if (!cls) return false;
    // Look up via templateSubjects length
    return templateSubjects.length <= 1;
  })();

  return (
    <div className="space-y-6" data-testid="sections-teachers-tab">
      {/* ============ SECTIONS ============ */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-outfit flex items-center gap-2">
            <Users size={18} className="text-[#0052FF]" weight="duotone" /> Sections par niveau ({schoolYear})
          </CardTitle>
          <Button onClick={applyAll} disabled={applying || loading}
                  className="bg-[#10B981] hover:bg-[#059669]" data-testid="apply-sections-btn">
            {applying ? '…' : <><CheckCircle size={16} className="mr-2" /> Appliquer aux classes</>}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[#64748B]">
            <b>Valeur 1</b> = classe unique (ex : « 6è »).
            <b> Valeur 2</b> = « 6è A » + « 6è B ». <b>Valeur 3</b> = A, B, C. Jusqu&apos;à 12.
          </p>
          {loading ? (
            <div className="p-4 text-center"><div className="animate-spin h-6 w-6 border-b-2 border-[#0052FF] rounded-full mx-auto" /></div>
          ) : (
            <div className="space-y-4">
              {cyclesInOrder.map(cyc => (
                <div key={cyc}>
                  <h4 className="text-xs uppercase font-medium text-[#64748B] mb-2">{CYCLE_LABELS[cyc] || cyc}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {(byCycle[cyc] || []).map(s => (
                      <div key={s.class_level} className="flex items-center gap-2 bg-[#F8FAFC] rounded px-3 py-2" data-testid={`section-${s.class_level}`}>
                        <span className="text-sm font-medium flex-1 truncate">{s.class_level}</span>
                        <Input
                          type="number" min="1" max="12"
                          value={s.count}
                          onChange={(e) => {
                            const v = Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1));
                            setSections(prev => prev.map(x => x.class_level === s.class_level ? { ...x, count: v } : x));
                          }}
                          onBlur={(e) => {
                            const v = Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1));
                            // Compare against the last count actually persisted on the server,
                            // not the locally-updated `s.count` (which always matches the input
                            // after onChange fires — the previous version of this handler was
                            // silently no-op because of this stale-closure trap).
                            const persisted = persistedCountsRef.current[s.class_level];
                            if (v !== persisted) setCount(s.class_level, v);
                          }}
                          onKeyDown={(e) => {
                            // Enter also triggers save so users don't have to click away.
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          disabled={savingLevel === s.class_level}
                          className="h-8 w-16 text-center text-sm"
                          data-testid={`section-count-${s.class_level}`}
                        />
                        <span className="text-[10px] text-[#94A3B8] w-14 truncate">
                          {s.count > 1 ? (s.sub_labels || []).join(',') : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ TEACHER ASSIGNMENTS ============ */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="font-outfit flex items-center gap-2">
            <ChalkboardTeacher size={18} className="text-[#0052FF]" weight="duotone" /> Affectation enseignants — matière & classe
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={downloadWorkload}
              className="border-[#F59E0B] text-[#B45309] hover:bg-amber-50"
              data-testid="download-workload-pdf-btn"
            >
              <Clock size={16} className="mr-2" weight="duotone" />
              Charge horaire (PDF)
            </Button>
            <Button
              variant="outline"
              onClick={downloadDistribution}
              className="border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF]/5"
              data-testid="download-distribution-pdf-btn"
            >
              <FilePdf size={16} className="mr-2" weight="duotone" />
              Répartition (PDF)
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <Label className="text-xs">Classe</Label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full border border-[#E2E8F0] rounded p-2 text-sm mt-1"
                data-testid="class-teachers-select"
              >
                <option value="">— Choisir une classe —</option>
                {classes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <p className="text-xs text-[#64748B]">
              <Star size={11} className="inline mr-0.5 text-amber-500" weight="duotone" />
              Un seul <b>Professeur Principal</b> par classe. Maternelle & Primaire = principal auto.
            </p>
          </div>

          {selectedClass && (
            loadingCls ? (
              <div className="p-4 text-center"><div className="animate-spin h-5 w-5 border-b-2 border-[#0052FF] rounded-full mx-auto" /></div>
            ) : templateSubjects.length === 0 ? (
              <p className="text-sm text-[#94A3B8] py-4">Aucun modèle de notation configuré pour cette classe. Passez par « Bulletins & Notes » d&apos;abord.</p>
            ) : (
              <div className="border border-[#E2E8F0] rounded overflow-hidden">
                <table className="w-full text-sm" data-testid="class-teachers-table">
                  <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase">
                    <tr>
                      <th className="text-left p-2">Matière</th>
                      <th className="text-left p-2">Enseignant</th>
                      <th className="text-center p-2 w-16">Principal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templateSubjects.map(subject => {
                      const a = getAssignment(subject);
                      return (
                        <tr key={subject} className="border-t border-[#F1F5F9]" data-testid={`assign-row-${subject}`}>
                          <td className="p-2 font-medium">{subject}</td>
                          <td className="p-2">
                            <select
                              value={a.teacher_id || ''}
                              onChange={(e) => assignTeacher(subject, e.target.value, a.is_principal)}
                              className="w-full border border-[#E2E8F0] rounded p-1.5 text-sm"
                              data-testid={`assign-teacher-${subject}`}
                            >
                              <option value="">— Non affecté —</option>
                              {teachers.map(t => (
                                <option key={t.id} value={t.id}>
                                  {t.last_name} {t.first_name}{t.subject ? ` (${t.subject})` : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2 text-center">
                            {a.teacher_id ? (
                              <input
                                type="radio"
                                name="principal"
                                checked={!!a.is_principal || isSingleTeacher}
                                disabled={isSingleTeacher}
                                onChange={() => assignTeacher(subject, a.teacher_id, true)}
                                className="h-4 w-4 accent-amber-500"
                                title={isSingleTeacher ? 'Enseignant principal automatique' : 'Marquer comme Professeur Principal'}
                                data-testid={`assign-principal-${subject}`}
                              />
                            ) : <span className="text-[#94A3B8]">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SectionsAndTeachersTab;
